/*
 * Request shapes and answers for the Marketing tabs ported from the WordPress Steward: Dashboard, Channels, Due again,
 * Email list and Guest posts.
 */
import { z } from "zod";
import type { Channel } from "./marketing-suite.js";
import type { ChannelRow } from "./marketing-suite.zod.js";
import { DUE_AGAIN_MAX_DAYS, PITCH_STATUSES, type PitchStatus } from "./marketing-tabs.js";

const text = (max: number) => z.string().trim().max(max);

// Dashboard ------------------------------------------------------------------------------------------------------------

export interface DashboardAnswer {
  /** Past 30 days. */
  leads30: number;
  newClients30: number;
  websiteRequests30: number;
  reviewsAsked30: number;
  reviewsOn: boolean;
  /** Set when the Google review link is missing, so review requests cannot go. */
  reviewLinkMissing: boolean;
  gbp: { connected: boolean; rating: number | null; reviewCount: number; waitingReplies: number };
  posts: { upcoming: number; next: { id: string; at: string; body: string }[] };
  emailList: { onList: number; new30: number };
  ranks: { keyword: string; average: number | null; before: number | null; top3: number; points: number }[];
  /** Where leads came from, past 30 days, most leads first (at most 6). */
  leadsBy: { channel: Channel; leads: number; newClients: number; spendCents: number }[];
}

// Channels -------------------------------------------------------------------------------------------------------------

export const channelsQuery = z.object({ range: z.enum(["month", "last", "30", "90", "year", "all"]).default("month") });
export type ChannelsQuery = z.infer<typeof channelsQuery>;

export interface ChannelTableRow extends ChannelRow {
  /** Everything clients from this channel have ever paid. */
  allTimeCents: number;
  costPerLeadCents: number | null;
}
export interface ChannelsAnswer {
  range: ChannelsQuery["range"];
  from: string;
  to: string;
  rows: ChannelTableRow[];
  totals: { spendCents: number; leads: number; newClients: number; revenueCents: number };
  /** Active clients with no lead source, newest first (at most 12), to tag. */
  untagged: { id: string; name: string }[];
}

// Due again ------------------------------------------------------------------------------------------------------------

export interface DueAgainRow {
  clientId: string;
  name: string;
  first: string | null;
  hasPhone: boolean;
  kind: string;
  last: string;
  days: number;
  /** Days past due (0 is due today). */
  over: number;
  /** When it was texted for this time the work came due, from here or the automatic message. */
  textedAt: string | null;
  /** The text with this client's fill-ins, for the Send box. */
  words: string;
}
export interface DueAgainAnswer {
  kinds: { kind: string; days: number; defaultDays: number }[];
  text: string;
  list: DueAgainRow[];
  /** Marketing texts go 9 AM to 7 PM in the company's time zone. */
  hoursOk: boolean;
  /** The automatic Due again message (Settings, Automatic messages). */
  autoOn: boolean;
}
export const dueAgainBody = z
  .object({
    days: z.record(text(80).min(1), z.number().int().min(0).max(DUE_AGAIN_MAX_DAYS)).optional(),
    text: text(320).min(1, "Write the text first.").optional(),
  })
  .strict();
export const dueSkipBody = z.object({ clientId: z.uuid(), kind: text(80).min(1) }).strict();
export const dueTextBody = z
  .object({ clientId: z.uuid(), kind: text(80).min(1), text: text(320).min(1, "Write the text first.") })
  .strict();

// Email list -----------------------------------------------------------------------------------------------------------

export interface EmailSignupRow {
  id: string;
  createdAt: string;
  email: string | null;
  name: string | null;
  zip: string | null;
  area: "in" | "out" | "unknown";
  page: string | null;
  unsub: boolean;
}
export interface EmailListAnswer {
  signups: EmailSignupRow[];
  onList: number;
  new30: number;
  inArea: number;
}
export const signupPatchBody = z.object({ unsub: z.boolean() }).strict();

// Guest posts ----------------------------------------------------------------------------------------------------------

export interface PitchRow {
  id: string;
  createdAt: string;
  name: string | null;
  email: string;
  site: string | null;
  topic: string | null;
  summary: string | null;
  past: string | null;
  status: PitchStatus;
}
export const pitchPatchBody = z.object({ status: z.enum(PITCH_STATUSES) }).strict();
